import React, { useState, useEffect } from 'react';
import {
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Chip,
  Link,
} from '@mui/material';
import { ReceiptLong as InvoiceIcon, Send as SendIcon, OpenInNew as OpenIcon } from '@mui/icons-material';
import logger from '../../utils/logger';

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * "Send Invoice" action for a job card. Opens a small dialog that previews
 * the invoice derived from the job (amount, status, share link), lets the
 * admin confirm or override the recipient, and emails it.
 *
 * Props:
 *   jobId      – required
 *   jobNumber  – optional, for labels
 *   compact    – render as an icon button instead of a text button
 *   onSent     – callback({ recipient, url }) after a successful send
 *   sentAt     – when the invoice was last emailed (from the job list), to
 *                show "already sent" before the dialog is opened
 */
const shortDateTime = (iso) => new Date(iso).toLocaleString('en-US', {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
});

const SendInvoiceButton = ({ jobId, jobNumber, compact = false, size = 'small', variant = 'outlined', onSent, disabled = false, sentAt = null }) => {
  const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
  const [open, setOpen] = useState(false);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(null);
  const [recipient, setRecipient] = useState('');
  const [sentNowAt, setSentNowAt] = useState(null); // sent from this button, before any reload

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    if (!open || !jobId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    setSent(null);
    fetch(`${apiBase}/api/jobs/${jobId}/invoice`, { headers: headers() })
      .then(async (res) => {
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.error || 'Failed to load invoice');
        return json;
      })
      .then((json) => {
        if (cancelled) return;
        setDoc(json);
        setRecipient(json.customer?.email || '');
      })
      .catch((err) => {
        logger.error('[SendInvoiceButton] preview failed:', err);
        if (!cancelled) setError(err.message || 'Failed to load invoice');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, jobId]);

  const handleSend = async () => {
    if (!EMAIL_RE.test(recipient)) {
      setError('Enter a valid recipient email');
      return;
    }
    setSending(true);
    setError('');
    try {
      const res = await fetch(`${apiBase}/api/jobs/${jobId}/send-invoice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers() },
        body: JSON.stringify({ to: recipient }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to send invoice');
      setSent(json);
      if (!isPaid) setSentNowAt(new Date().toISOString());
      if (onSent) onSent(json);
    } catch (err) {
      logger.error('[SendInvoiceButton] send failed:', err);
      setError(err.message || 'Failed to send invoice');
    } finally {
      setSending(false);
    }
  };

  const close = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (!sending) setOpen(false);
  };

  const openDialog = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setOpen(true);
  };

  const isPaid = doc?.status === 'PAID';
  const invoiceSends = (doc?.sendHistory || []).filter((h) => h.kind === 'invoice');
  const alreadySent = !isPaid && invoiceSends.length > 0;
  const label = isPaid ? 'Send Receipt' : alreadySent ? 'Send Again' : 'Send Invoice';
  const lastSentAt = sentNowAt || sentAt || (invoiceSends[0] && invoiceSends[0].sentAt);

  return (
    <>
      {compact ? (
        <Tooltip title={lastSentAt ? `Invoice sent ${shortDateTime(lastSentAt)} — click to see history or resend` : 'Send invoice'}>
          <span>
            <IconButton size={size} onClick={openDialog} disabled={disabled} color={lastSentAt ? 'success' : 'primary'}>
              <InvoiceIcon fontSize={size === 'small' ? 'small' : 'medium'} />
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <Tooltip title={lastSentAt ? `Last sent ${shortDateTime(lastSentAt)}` : ''}>
          <span>
            <Button size={size} variant={variant} startIcon={<InvoiceIcon />} onClick={openDialog} disabled={disabled}>
              {lastSentAt ? 'Invoice Sent · Resend' : 'Send Invoice'}
            </Button>
          </span>
        </Tooltip>
      )}

      <Dialog open={open} onClose={close} maxWidth="xs" fullWidth onClick={(e) => e.stopPropagation()}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InvoiceIcon color="primary" />
          {label}{jobNumber ? ` · ${jobNumber}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} /></Box>
          ) : sent ? (
            <Alert severity="success">
              Sent to <strong>{sent.recipient}</strong>.
              {sent.url && (
                <Box mt={1}>
                  <Link href={sent.url} target="_blank" rel="noopener" underline="hover">
                    Open the hosted invoice <OpenIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} />
                  </Link>
                </Box>
              )}
            </Alert>
          ) : (
            <Box display="flex" flexDirection="column" gap={2}>
              {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

              {doc && (
                <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                  <Box>
                    <Typography variant="subtitle2">{doc.number}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {doc.customer?.name || 'Unknown customer'}
                    </Typography>
                  </Box>
                  <Box textAlign="right">
                    <Typography variant="subtitle1" fontWeight="bold">{money(doc.total)}</Typography>
                    <Chip
                      size="small"
                      label={doc.status === 'PAID' ? 'Paid' : doc.status === 'REFUNDED' ? 'Refunded' : `Due ${money(doc.amountDue)}`}
                      color={doc.status === 'PAID' ? 'success' : doc.status === 'REFUNDED' ? 'default' : 'warning'}
                    />
                  </Box>
                </Box>
              )}

              {alreadySent && (
                <Alert severity="warning">
                  This invoice was already sent {invoiceSends.length === 1 ? 'once' : `${invoiceSends.length} times`}.
                  Sending again emails the same invoice to the customer.
                </Alert>
              )}

              {doc?.sendHistory?.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>History</Typography>
                  {doc.sendHistory.map((h, i) => (
                    <Typography key={i} variant="caption" display="block" color="text.secondary">
                      {h.kind === 'receipt' ? 'Receipt' : 'Invoice'} sent {shortDateTime(h.sentAt)} to {h.recipient}
                    </Typography>
                  ))}
                </Box>
              )}

              {doc && Number(doc.total) === 0 && (
                <Alert severity="warning">This job has no amount. Set a price on the job before invoicing.</Alert>
              )}

              <TextField
                label="Send to"
                type="email"
                size="small"
                fullWidth
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                helperText={doc?.customer?.email ? 'Customer email on file. Change it to send elsewhere.' : 'No email on file for this customer. Enter one.'}
                error={!!recipient && !EMAIL_RE.test(recipient)}
              />

              {doc?.publicUrl && (
                <Link href={doc.publicUrl} target="_blank" rel="noopener" underline="hover" variant="body2">
                  Preview what the customer will see <OpenIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} />
                </Link>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={sending}>{sent ? 'Done' : 'Cancel'}</Button>
          {!sent && (
            <Button
              variant="contained"
              onClick={handleSend}
              disabled={loading || sending || !doc || !EMAIL_RE.test(recipient) || Number(doc?.total) === 0}
              startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            >
              {sending ? 'Sending...' : label}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SendInvoiceButton;
